import { type FC } from 'react';
import {
    Alert,
    Box,
    LinearProgress,
    Snackbar,
    Typography,
} from '@mui/material';

import { type ExportProgress } from '#apps/client-web/features/export/hooks/use-export-template-mutation';

export type ExportProgressSnackbarProps = {
    open: boolean;
    progress: ExportProgress | null;
    onClose: () => void;
};

/**
 * Shows a bottom-anchored snackbar with a determinate progress bar while
 * the audit export is in progress. Auto-closes on completion (100%).
 */
export const ExportProgressSnackbar: FC<ExportProgressSnackbarProps> = ({
    open,
    progress,
    onClose,
}) => {
    const percent = progress?.percent ?? 0;
    const label = progress?.label ?? 'Starting export…';
    const isComplete = percent >= 100;

    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={isComplete ? 3000 : null}
            onClose={isComplete ? onClose : undefined}>
            <Alert
                severity={isComplete ? 'success' : 'info'}
                icon={isComplete ? undefined : false}
                onClose={isComplete ? onClose : undefined}
                sx={{ width: '100%', minWidth: 320 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}>
                        <Typography variant="body2">
                            {isComplete ? 'Export ready' : label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {percent}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={percent}
                        color={isComplete ? 'success' : 'primary'}
                    />
                </Box>
            </Alert>
        </Snackbar>
    );
};
